import { useEffect, useRef } from 'react';
import { useDateRangeStore } from '@/stores/dateRangeStore';
import { startOfDay } from 'date-fns';

/**
 * Hook that automatically refreshes date ranges when the day changes.
 * Useful for TV dashboards that run 24/7 without manual interaction.
 */
export function useDateAutoRefresh() {
  const { preset, setPreset } = useDateRangeStore();
  const lastDateRef = useRef<string>(startOfDay(new Date()).toISOString());

  useEffect(() => {
    // Check every minute if the day has changed
    const checkDateChange = () => {
      const currentDay = startOfDay(new Date()).toISOString();

      if (currentDay !== lastDateRef.current) {
        console.log('[DateAutoRefresh] Day changed, refreshing date range');
        lastDateRef.current = currentDay;

        // Re-apply the current preset to get fresh dates
        // This will update 'today' to the new day, 'this_week' to include new day, etc.
        if (preset !== 'custom') {
          setPreset(preset);
        }
      }
    };

    // Check immediately on mount
    checkDateChange();

    // Check every minute
    const interval = setInterval(checkDateChange, 60 * 1000);

    // Also check when tab becomes visible (in case device was sleeping)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkDateChange();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [preset, setPreset]);
}
